// @services/stubs/StubStatementClassifierClient.ts
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import {
    decodeStatementClassifierPreviewResponse,
    decodeStatementClassifierRefusal,
    decodeStatementClassifierStatusResponse,
    decodeStatementClassifierSubmitJobResponse,
    decodeStatementClassifierWorkbookFileName,
    type StatementClassifierApiDecodeResult,
    type StatementClassifierClient,
    type StatementClassifierClientError,
    type StatementClassifierGuidance,
    type StatementClassifierJobId,
    type StatementClassifierPreviewResult,
    type StatementClassifierStatusResult,
    type StatementClassifierSubmitJobResult,
    type StatementClassifierWorkbookResult,
} from "@services/clients/StatementClassifierClient";
import { taskEitherLeft, taskEitherOf } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export type StubStatementClassifierFailureMode =
    | "none"
    | "invalidPayload"
    | "unknownJob"
    | "workbookUnavailable"
    | "serviceUnavailable"
    | "unsupportedFileType"
    | "noExtractableText"
    | "processingFailed"
    | "downloadUnavailable"
    | "networkError"
    | "responseDecodeError";

const STUB_JOB_ID = "019e6191-4751-7424-9cbf-8d8cc684bad3";
const STUB_TRACE = {
    traceId: "stub-statement-classifier-trace",
};
const STUB_WORKBOOK_ARTIFACT_ID = "stub-statement-classifier-workbook";
const STUB_WORKBOOK_FILE_NAME = "statement-classifier-stub.xlsx";
const jsonNull = () => JSON.parse("null");

const decodeOrFailure = <T>(
    result: StatementClassifierApiDecodeResult<T>,
): TaskEitherTs<StatementClassifierClientError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value);
    }

    return taskEitherLeft(failureFromMode("responseDecodeError", result.message));
};

const buildFailure = (
    error: Exclude<StubStatementClassifierFailureMode, "none">,
    message: string,
): StatementClassifierClientError => {
    const decoded = decodeStatementClassifierRefusal({
        error,
        message,
        trace: STUB_TRACE,
    });

    if (decoded.type === "ok") {
        return decoded.value;
    }

    return {
        error: "responseDecodeError",
        message: "Unable to decode Statement Classifier refusal.",
        trace: Option.none(),
    };
};

const failureFromMode = (
    mode: Exclude<StubStatementClassifierFailureMode, "none">,
    detail?: string,
): StatementClassifierClientError => {
    switch (mode) {
        case "invalidPayload":
            return buildFailure("invalidPayload", "Statement Classifier request was missing required files or guidance.");
        case "unknownJob":
            return buildFailure("unknownJob", "Statement Classifier job was not found.");
        case "workbookUnavailable":
        case "downloadUnavailable":
            return buildFailure("downloadUnavailable", "Statement Classifier workbook is not available yet.");
        case "serviceUnavailable":
            return buildFailure("serviceUnavailable", "Statement Classifier service is unavailable.");
        case "unsupportedFileType":
            return buildFailure("unsupportedFileType", "One or more selected files are not supported.");
        case "noExtractableText":
            return buildFailure("noExtractableText", "No extractable text was found in the selected statements.");
        case "processingFailed":
            return buildFailure("processingFailed", "Statement classification failed before the workbook was ready.");
        case "networkError":
            return buildFailure("networkError", "Statement Classifier service could not be reached.");
        case "responseDecodeError":
            return buildFailure("responseDecodeError", detail ?? "Unable to decode Statement Classifier response.");
    }
};

const maybeFailure = <T>(
    mode: StubStatementClassifierFailureMode,
    success: () => TaskEitherTs<StatementClassifierClientError, T>,
): TaskEitherTs<StatementClassifierClientError, T> => {
    if (mode === "none") {
        return success();
    }

    return taskEitherLeft(failureFromMode(mode));
};

const statusPayload = () => ({
    jobId: STUB_JOB_ID,
    phase: "Completed",
    failure: jsonNull(),
    counts: {
        submittedFiles: 1,
        previewRows: 2,
    },
    workbook: {
        id: STUB_WORKBOOK_ARTIFACT_ID,
        kind: "workbook",
        fileName: STUB_WORKBOOK_FILE_NAME,
        mediaType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        byteSize: 128,
    },
});

const submitPayload = () => ({
    jobId: STUB_JOB_ID,
    status: statusPayload(),
    trace: STUB_TRACE,
});

const statusResponsePayload = () => ({
    status: statusPayload(),
    trace: STUB_TRACE,
});

const previewPayload = () => ({
    rows: [
        {
            fileName: "statement-classifier-stub.txt",
            date: "2026-05-01",
            description: "Synthetic transaction A",
            amount: 10,
            classification: "Stub Category A",
            confidence: 0.94,
            reasoning: "Deterministic stub row for preview rendering.",
        },
        {
            fileName: "statement-classifier-stub.txt",
            date: "2026-05-02",
            description: "Synthetic transaction B",
            amount: 20,
            classification: "Stub Category B",
            confidence: 0.88,
            reasoning: "Deterministic stub row for table-state coverage.",
        },
    ],
    trace: STUB_TRACE,
});

export class StubStatementClassifierClient implements StatementClassifierClient {
    private readonly failureMode: StubStatementClassifierFailureMode;

    constructor(failureMode: OptionTs<StubStatementClassifierFailureMode>) {
        this.failureMode = Option.fold(failureMode, () => "none", (value) => value);
    }

    submitJob(
        files: File[],
        guidance: StatementClassifierGuidance,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierSubmitJobResult> {
        if (files.length === 0) {
            return taskEitherLeft(failureFromMode("invalidPayload"));
        }

        if (Option.fold(guidance.sections, () => false, (sections) => sections.length > 1000)) {
            return taskEitherLeft(failureFromMode("invalidPayload"));
        }

        return maybeFailure(this.failureMode, () =>
            decodeOrFailure(decodeStatementClassifierSubmitJobResponse(submitPayload())),
        );
    }

    getJobStatus(
        _jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierStatusResult> {
        return maybeFailure(this.failureMode, () =>
            decodeOrFailure(decodeStatementClassifierStatusResponse(statusResponsePayload())),
        );
    }

    getPreview(
        _jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierPreviewResult> {
        return maybeFailure(this.failureMode, () =>
            decodeOrFailure(decodeStatementClassifierPreviewResponse(previewPayload())),
        );
    }

    downloadWorkbook(
        jobId: StatementClassifierJobId,
    ): TaskEitherTs<StatementClassifierClientError, StatementClassifierWorkbookResult> {
        return maybeFailure(this.failureMode, () => {
            const fileName = decodeStatementClassifierWorkbookFileName(STUB_WORKBOOK_FILE_NAME);
            if (fileName.type === "error") {
                return taskEitherLeft(failureFromMode("responseDecodeError", fileName.message));
            }

            return taskEitherOf({
                jobId,
                fileName: fileName.value,
                content: new Blob(["stub workbook"], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
            });
        });
    }
}
